import type { Deal, Job, Me, Proposal } from '@nextdone/shared';

export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

let token: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setSessionToken(next: string | null): void {
  token = next;
}

export function getSessionToken(): string | null {
  return token;
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

type Envelope<T> = { data?: T; error?: string };

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (init.body != null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let res: Response;
  try {
    res = await fetch(path, { ...init, headers });
  } catch {
    throw new ApiError('Network unread — the ledger did not respond.', 0);
  }

  let body: Envelope<T> | null = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text) as Envelope<T>;
    } catch {
      body = null;
    }
  }

  if (res.status === 401) {
    onUnauthorized?.();
    throw new ApiError(body?.error ?? 'Unauthorized', 401);
  }
  if (!res.ok) {
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status);
  }
  if (body?.data === undefined) {
    throw new ApiError('Empty response', res.status);
  }
  return body.data;
}

function jsonBody(body: unknown): RequestInit {
  return { body: JSON.stringify(body) };
}

export const api = {
  auth(initData: string): Promise<{ token: string; user: { id: number } }> {
    return request('/api/auth', {
      method: 'POST',
      headers: { 'X-Telegram-Init-Data': initData },
    });
  },
  profile(): Promise<Me> {
    return request('/api/profile');
  },
  jobs(): Promise<Job[]> {
    return request('/api/jobs?limit=100');
  },
  job(id: string): Promise<Job> {
    return request(`/api/jobs/${encodeURIComponent(id)}`);
  },
  createJob(body: {
    title: string;
    category: Job['category'];
    description: string;
    milestones: Job['milestones'];
    autoRelease: boolean;
  }): Promise<Job> {
    return request('/api/jobs', { method: 'POST', ...jsonBody(body) });
  },
  createProposal(
    jobId: string,
    body: { price: number; days: number; message: string },
  ): Promise<Proposal> {
    return request(`/api/jobs/${encodeURIComponent(jobId)}/proposals`, {
      method: 'POST',
      ...jsonBody(body),
    });
  },
  acceptProposal(jobId: string, proposalId: string): Promise<Deal> {
    return request(
      `/api/jobs/${encodeURIComponent(jobId)}/proposals/${encodeURIComponent(proposalId)}/accept`,
      { method: 'POST' },
    );
  },
  declineProposal(jobId: string, proposalId: string): Promise<Proposal> {
    return request(
      `/api/jobs/${encodeURIComponent(jobId)}/proposals/${encodeURIComponent(proposalId)}/decline`,
      { method: 'POST' },
    );
  },
  deals(): Promise<Deal[]> {
    return request('/api/deals');
  },
  deal(id: string): Promise<Deal> {
    return request(`/api/deals/${encodeURIComponent(id)}`);
  },
  deliver(dealId: string, index: number, note: string): Promise<Deal> {
    return request(`/api/deals/${encodeURIComponent(dealId)}/milestones/${index}/deliver`, {
      method: 'POST',
      ...jsonBody({ note }),
    });
  },
  release(dealId: string, index: number): Promise<Deal> {
    return request(`/api/deals/${encodeURIComponent(dealId)}/milestones/${index}/release`, {
      method: 'POST',
    });
  },
  dispute(dealId: string, reason: string): Promise<Deal> {
    return request(`/api/deals/${encodeURIComponent(dealId)}/dispute`, {
      method: 'POST',
      ...jsonBody({ reason }),
    });
  },
  withdrawDispute(dealId: string): Promise<Deal> {
    return request(`/api/deals/${encodeURIComponent(dealId)}/dispute/withdraw`, {
      method: 'POST',
    });
  },
  rate(dealId: string, rating: number): Promise<Deal> {
    return request(`/api/deals/${encodeURIComponent(dealId)}/rate`, {
      method: 'POST',
      ...jsonBody({ rating }),
    });
  },
  sync(dealId: string): Promise<Deal> {
    return request(`/api/deals/${encodeURIComponent(dealId)}/sync`, { method: 'POST' });
  },
};
